import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles
} from '@material-ui/core/styles';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  additionalCostData,
  btnColor,
  mainBlueColor,
  workingBudgetVendor,
  inputPriceState
} from '../../GlobalState';
import { parsePrice } from '../../utils';

const StyledTableCell = withStyles(theme => ({
  root: {
    borderBottom: 'none'
  },
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    fontSize: 16
  }
}))(TableCell);

const StyledTableCellWhite = withStyles(theme => ({
  root: {
    borderBottom: 'none',
    backgroundColor: '#F5F5F5 !important'
  },
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    fontSize: 16
  }
}))(TableCell);

const StyledIconCell = withStyles(theme => ({
  root: {
    padding: 0,
    borderBottom: 'none'
  }
}))(TableCell);

const StyledIconCellWhite = withStyles(theme => ({
  root: {
    padding: 0,
    borderBottom: 'none',
    backgroundColor: '#F5F5F5 !important'
  }
}))(TableCell);

// カラムの横幅
const colWidth = {
  icon: 50, // 階層アイコン
  itemName: 225, // 項目
  details: 225, // 仕様・詳細
  budgetQuantity: 100, // 数量
  budgetUnit: 75, // 単位
  estimated: 260,
  estimatedPrice: 130, // 見積 単価
  estimatedAmount: 130, // 見積 金額
  cost: 260,
  costPrice: 130, // 原価 単価
  costAmount: 130 // 原価 金額
};

type TProps = {
  isDetailShow: boolean;
  mode: number;
  setSelectItems?: any;
};

/**
 * テーブルヘッダー
 * @constructor
 */
export const ItemTableHeader: React.FC<TProps> = (props: TProps) => {
  const { isDetailShow, mode, setSelectItems } = props;

  const [mainColor] = useRecoilState(mainBlueColor);
  const [buttonColor] = useRecoilState(btnColor);
  const classes = useStyles({ mainColor: mainColor });
  const [isAllSelect, setIsAllSelect] = useState<boolean>(false);
  const [additionalCost, setAdditionalCost] =
    useRecoilState(additionalCostData);

  const [vendor, setVendor] = useRecoilState(workingBudgetVendor);

  const setInputPriceState = useSetRecoilState(inputPriceState);
  const setState = (data: number) => {
    setInputPriceState(data);
  };

  //setStateをjsx内で直接使用してエラーとなっていたため、useEffectでv.totalを設定する仕様に変更
  useEffect(() => {
    if (vendor && vendor.length > 0) {
      vendor.forEach(v => {
        setState(v.total); // 各要素の total を設定
      });
    }
  }, [vendor]); // vendor が変更されたときに useEffect が呼ばれる

  return (
    <TableHead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
      <TableRow style={{ height: 70 }}>
        <StyledIconCellWhite
          className={classes.stickyHeaderTitleNone}
          style={{ width: colWidth.icon, left: 0, zIndex: 5 }}
        ></StyledIconCellWhite>
        <StyledTableCellWhite
          className={classes.stickyHeaderTitleNone}
          align='left'
          style={{ width: colWidth.itemName, left: 50 }}
        ></StyledTableCellWhite>
        {isDetailShow && (
          <>
            <StyledTableCellWhite
              className={classes.stickyHeaderTitleNone}
              align='left'
              style={{ width: colWidth.details, left: 275 }}
            ></StyledTableCellWhite>
          </>
        )}
        <StyledTableCellWhite
          className={classes.stickyHeaderTitleNone}
          align='left'
          style={{
            width: colWidth.budgetQuantity,
            left: 500 - (isDetailShow ? 0 : 225)
          }}
        ></StyledTableCellWhite>
        <StyledTableCellWhite
          className={classes.stickyHeaderTitleNone}
          align='left'
          style={{
            width: colWidth.budgetUnit,
            left: 600 - (isDetailShow ? 0 : 225)
          }}
        ></StyledTableCellWhite>
        {isDetailShow && (
          <>
            <StyledTableCell
              className={classes.stickyHeader}
              align='left'
              colSpan={2}
              style={{ width: colWidth.estimated, left: 675 }}
            >
              見積
            </StyledTableCell>
          </>
        )}

        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          colSpan={2}
          style={{ width: colWidth.cost, left: 935 - (isDetailShow ? 0 : 485) }}
        >
          {mode == 0 ? '見積原価' : '追加原価'}
        </StyledTableCell>

        {vendor &&
          vendor.length > 0 &&
          vendor.map(v => (
            <StyledTableCell className={classes.vendorHeader} align='left'>
              {v.name}
            </StyledTableCell>
          ))}
      </TableRow>
      <TableRow style={{ height: 70 }}>
        {mode == 0 ? (
          <StyledIconCell
            className={classes.stickyHeaderTitleNone}
            style={{ width: colWidth.icon, left: 0, zIndex: 5 }}
            align='center'
          ></StyledIconCell>
        ) : (
          <StyledIconCell
            className={classes.stickyHeaderTitleNone}
            style={{ width: colWidth.icon, left: 0, zIndex: 5 }}
            align='center'
          >
            <Checkbox
              checked={isAllSelect}
              onClick={() => {
                setIsAllSelect(!isAllSelect);
                if (isAllSelect) {
                  setSelectItems([]);
                } else {
                  const ids = additionalCost.map(item => item.id);
                  setSelectItems(ids);
                }
              }}
              style={{ color: isAllSelect ? buttonColor : '#FFFFFF' }}
            />
          </StyledIconCell>
        )}
        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          style={{ width: colWidth.itemName, left: 50 }}
        >
          項目名
        </StyledTableCell>
        {isDetailShow && (
          <>
            <StyledTableCell
              className={classes.stickyHeader}
              align='left'
              style={{ width: colWidth.details, left: 275 }}
            >
              仕様・詳細
            </StyledTableCell>
          </>
        )}
        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          style={{
            width: colWidth.budgetQuantity,
            left: 500 - (isDetailShow ? 0 : 225)
          }}
        >
          原価数量
        </StyledTableCell>
        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          style={{
            width: colWidth.budgetUnit,
            left: 600 - (isDetailShow ? 0 : 225)
          }}
        >
          原価単位
        </StyledTableCell>
        {isDetailShow && (
          <>
            <StyledTableCell
              className={classes.stickyHeader}
              align='left'
              style={{ width: colWidth.estimatedPrice, left: 675 }}
            >
              見積単価
            </StyledTableCell>
            <StyledTableCell
              className={classes.stickyHeader}
              align='left'
              style={{ width: colWidth.estimatedAmount, left: 805 }}
            >
              見積金額
            </StyledTableCell>
          </>
        )}
        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          style={{
            width: colWidth.costPrice,
            left: 935 - (isDetailShow ? 0 : 485)
          }}
        >
          単価
        </StyledTableCell>
        <StyledTableCell
          className={classes.stickyHeader}
          align='left'
          style={{
            width: colWidth.costAmount,
            left: 1065 - (isDetailShow ? 0 : 485)
          }}
        >
          金額
        </StyledTableCell>
        {vendor &&
          vendor.length > 0 &&
          vendor.map(v => (
            <StyledTableCell
              className={classes.vendorHeaderTotal}
              align='right'
            >
              {mode == 0 ? parsePrice(v.total) : parsePrice(v.additional_cost)}
            </StyledTableCell>
          ))}
      </TableRow>
    </TableHead>
  );
};

type TStyleProps = {
  mainColor: string;
};

const useStyles = makeStyles<Theme, TStyleProps>((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      padding: 10
    },
    stickyHeader: {
      position: 'sticky',
      background: '#0099CC',
      color: '#FFFFFF',
      padding: 0,
      fontWeight: 'bold',
      fontSize: 16,
      border: '1px #CCCCCC solid',
      paddingLeft: 10
    },
    stickyHeaderTitleNone: {
      position: 'sticky',
      background: '#0099CC',
      color: '#FFFFFF',
      padding: 0,
      fontWeight: 'bold',
      fontSize: 16
    },
    vendorHeader: {
      width: 150,
      color: '#FFFFFF',
      background: '#007ecc',
      zIndex: 1,
      fontWeight: 'bold',
      border: '1px #CCCCCC solid',
      paddingLeft: 10
    },
    vendorHeaderTotal: {
      width: 150,
      color: '#FFFFFF',
      background: '#007ecc',
      zIndex: 1,
      fontWeight: 'bold',
      border: '1px #CCCCCC solid',
      paddingRight: 10
    }
  })
);
